import * as React from 'react';
import { Stack } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { selectUser, useCacheQuery } from 'gossamer-front-end';
import { BookingList } from './WebsiteAccountLists';
import { ReportUserView } from 'cb-model';
import classes from './PortfolioTile.module.css';

const getWeekCommencing = (weeksBack: number) => {
  const now = new Date();
  now.setDate(now.getDate() - weeksBack * 7);
  return now.toISOString();
};

export const WebsiteAccountPortfolioPage = (): React.JSX.Element => {
  const user = useSelector(selectUser);
  const now = React.useRef(new Date().toISOString());
  const midpWeekCommencing = React.useRef(getWeekCommencing(4));
  const fromWeekCommencing = React.useRef(getWeekCommencing(8));
  // get last 8 weeks of data
  const reportQuery = useCacheQuery(ReportUserView, {
    where: {
      and: [
        { fieldId: 'user_id', operator: 'eq', value: user?.userId || '' },
        { fieldId: 'week_commencing', operator: 'gt', value: fromWeekCommencing.current },
        { fieldId: 'week_commencing', operator: 'lt', value: now.current },
      ],
    },
  });

  const sum = (property: 'event_count' | 'post_event_count' | 'attendance_count' | 'feedback_count') =>
    (reportQuery.data ?? []).reduce(
      (acc, report) => {
        const groupIndex = report.week_commencing >= midpWeekCommencing.current ? 0 : 1;
        acc[groupIndex] = (acc[groupIndex] || 0) + (report[property] ?? 0);
        return acc;
      },
      [0, 0],
    );

  const totalEvents = sum('event_count');
  const trackedEvents = sum('post_event_count');
  const attendance = sum('attendance_count');
  const feedback = sum('feedback_count');
  return (
    <>
      <Stack direction="horizontal" gap={3} className="mb-3" style={{ justifyContent: 'flex-end' }}>
        <div>Over the last 4 weeks:</div>
        <PortfolioTile label="Total Events" last4weekValue={totalEvents[0]} prev4weekValue={totalEvents[1]} />
        <PortfolioTile label="Tracked Events" last4weekValue={trackedEvents[0]} prev4weekValue={trackedEvents[1]} />
        <PortfolioTile label="Attendance" last4weekValue={attendance[0]} prev4weekValue={attendance[1]} />
        <PortfolioTile label="Feedback" last4weekValue={feedback[0]} prev4weekValue={feedback[1]} />
      </Stack>
      <BookingList
        query={{
          where: {
            and: [
              { fieldId: 'user_id', operator: 'eq', value: user?.userId || '' },
              { fieldId: 'end_at', operator: 'lt', value: now.current },
            ],
          },
          order: [['end_at', 'DESC']],
          offset: 0,
          limit: 100,
        }}
        emptyText="No past events yet."
      />
    </>
  );
};

interface PortfolioTileProps {
  label: string;
  last4weekValue: number;
  prev4weekValue: number;
}

const PortfolioTile = (props: PortfolioTileProps): React.JSX.Element => {
  const change = props.last4weekValue - props.prev4weekValue;
  const changePercentage = props.prev4weekValue !== 0 ? (change / props.prev4weekValue) * 100 : NaN;
  const changeClass = change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral';
  return (
    <div className={classes.PortfolioTile}>
      <div className="label">{props.label}</div>
      <div className="main">{props.last4weekValue}</div>
      <div className={changeClass}>
        {change > 0 ? '↑ ' : change < 0 ? '↓ ' : ''}
        {change === 0 ? 'no change' : isNaN(changePercentage) ? 'From 0' : changePercentage.toFixed(0) + '%'}
      </div>
    </div>
  );
};
